import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import {
  Truck, HandCoins, GasPump, MapTrifold, ChartPie, FileText, Handshake, Signature, Gift,
  CheckCircle, Star, DeviceMobile, CloudCheck, WhatsappLogo, ArrowRight, Rocket,
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: HandCoins, title: "Fretes & Viagens", desc: "Registra fretes, calcula automático R$/ton ↔ Total, comissão e adiantamentos." },
  { icon: GasPump, title: "Abastecimentos", desc: "Auto-calcula média km/L, alerta vermelho/amarelo/verde por eficiência." },
  { icon: Handshake, title: "Acerto Final Automático", desc: "Ao chegar na empresa o acerto já tá pronto — só imprimir, PDF ou WhatsApp." },
  { icon: MapTrifold, title: "GPS em Tempo Real", desc: "Empresa vê a localização do caminhão ao vivo, com histórico de rotas." },
  { icon: Signature, title: "Canhoto Digital", desc: "Motorista bate foto do CTe assinado no celular. Baixa MDFe rastreada." },
  { icon: ChartPie, title: "Análise & Ranking", desc: "Ranking de motoristas por km/L. Média por veículo. Previsão de manutenção." },
  { icon: FileText, title: "PDF + Excel", desc: "Relatórios profissionais em PDF e planilhas Excel do mês pro contador." },
  { icon: CloudCheck, title: "Offline + Web + App", desc: "Funciona sem internet. Instalável como app. Sincroniza quando conecta." },
];

const HOW = [
  { n: 1, title: "Cadastre em 60 segundos", desc: "Empresa, motorista autônomo ou motorista empregado. Sem cartão pra começar." },
  { n: 2, title: "Lance viagens durante o mês", desc: "Motorista lança fretes, abastecimentos e despesas. Tudo sincroniza em tempo real." },
  { n: 3, title: "Acerto pronto ao chegar", desc: "O app calcula sozinho. É só imprimir, salvar PDF ou mandar pelo WhatsApp." },
];

const TESTIMONIALS = [
  { name: "Carlos M.", role: "Autônomo, PR", text: "Antes eu perdia domingo inteiro no caderninho. Hoje o acerto já sai pronto na chegada em casa." },
  { name: "Transp. Silva", role: "Empresa, MG", text: "3 motoristas, 5 caminhões. O ranking de km/L virou nossa reunião mensal — economizamos 800L de diesel no primeiro mês." },
  { name: "João P.", role: "Empregado, SP", text: "Meu patrão nem tinha app. Comecei a usar sozinho, mostrei o acerto pra ele e agora ele tá querendo assinar." },
];

const FAQ = [
  { q: "Preciso ter cartão pra testar?", a: "Não. 7 dias grátis sem cartão. Se não assinar até o fim do trial, a conta é excluída automaticamente e você pode se cadastrar de novo depois." },
  { q: "Funciona offline?", a: "Sim. É um PWA — instala no celular como app nativo e sincroniza quando conectar." },
  { q: "Posso pagar por PIX?", a: "Sim, PIX à vista mensal com aviso 8 dias antes e reativação automática ao pagar. Ou cartão em até 3x." },
  { q: "Quanto eu ganho indicando?", a: "50% OFF no seu próximo pagamento — até 3 indicações. E quem você indica também ganha 50%." },
];

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`w-full py-16 md:py-24 ${className}`}>
    <div className="max-w-6xl mx-auto px-6">{children}</div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2"><Logo size={40} /><div className="hidden sm:block"><div className="font-extrabold text-blue-900 text-sm leading-none" style={{ fontFamily: "Manrope" }}>ACERTO RÁPIDO</div><div className="text-[9px] tracking-[0.3em] text-blue-700/70">JVO</div></div></Link>
          <div className="flex items-center gap-2">
            <a href="#precos" className="text-sm text-slate-700 hover:text-blue-700 hidden md:inline font-semibold">Preços</a>
            <a href="#como-funciona" className="text-sm text-slate-700 hover:text-blue-700 hidden md:inline font-semibold">Como funciona</a>
            <Link to="/como-indicar" className="text-sm text-emerald-700 hover:text-emerald-800 hidden md:inline font-semibold">Indique e ganhe</Link>
            <Link to="/login" data-testid="nav-login" className="text-sm text-slate-700 hover:text-blue-700 font-semibold px-3">Entrar</Link>
            <Link to="/assinar" data-testid="nav-assinar" className="text-sm text-emerald-700 hover:text-emerald-800 hidden md:inline font-bold">Assinar já</Link>
            <Link to="/register" data-testid="nav-register" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-lg">Testar grátis</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <Section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ backgroundImage: "url(/road-banner.png)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.15 }} />
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="bg-yellow-400 text-blue-900 mb-4 gap-1"><Star size={12} weight="fill" /> 7 dias grátis · sem cartão</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight" style={{ fontFamily: "Manrope" }}>
              Acerto de viagens<br /><span className="text-blue-700">em minutos</span>, não horas
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
              O app definitivo pra caminhoneiros e transportadoras. Motorista lança durante a viagem, empresa vê em tempo real e o acerto já sai pronto na chegada — <strong>PDF, WhatsApp, PIX, cartão em 3x.</strong>
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/register"><Button data-testid="hero-cta" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-2 h-12 px-6 text-base font-bold w-full sm:w-auto"><Rocket size={20} weight="fill" /> Criar conta grátis</Button></Link>
              <a href="#precos"><Button variant="outline" className="h-12 px-6 border-2 gap-2 w-full sm:w-auto">Ver preços <ArrowRight size={16} /></Button></a>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Funciona offline</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> PIX + Cartão até 3x</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Cancele quando quiser</span>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="relative">
              <div className="absolute inset-0 blur-3xl bg-blue-400/20 rounded-full" />
              <div className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl aspect-square bg-gradient-to-br from-blue-50 to-emerald-50 overflow-hidden">
                <video
                  data-testid="hero-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster="/logo-acerto.png"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                >
                  <source src="/promo-jvo.webm" type="video/webm" />
                  <source src="/promo-jvo.mp4" type="video/mp4" />
                </video>
                <img
                  src="/logo-acerto.png"
                  alt="Acerto Rápido JVO"
                  className="absolute bottom-4 right-4 w-16 h-16 object-contain drop-shadow-xl pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Como funciona */}
      <Section id="como-funciona" className="bg-slate-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Como funciona</h2>
          <p className="text-slate-600 mt-2">3 passos e você já tá economizando horas de canetada</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {HOW.map((h) => (
            <Card key={h.n} className="p-6 border-2 border-blue-100">
              <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-extrabold mb-4" style={{ fontFamily: "Manrope" }}>{h.n}</div>
              <h3 className="font-extrabold text-lg" style={{ fontFamily: "Manrope" }}>{h.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5">{h.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Features */}
      <Section id="features">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Tudo o que você precisa</h2>
          <p className="text-slate-600 mt-2">Fim das planilhas soltas e cadernos rabiscados</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <Card key={f.title} className="p-5 border hover:border-blue-300 hover:shadow-md transition-all">
              <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
                <f.icon size={24} weight="duotone" />
              </div>
              <h3 className="font-bold text-base">{f.title}</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Preços */}
      <Section id="precos" className="bg-gradient-to-br from-blue-50 to-white">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Preços simples e diretos</h2>
          <p className="text-slate-600 mt-2">7 dias grátis. Sem cartão pra começar. Cancele quando quiser.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <Card className="p-8 border-2 hover:border-blue-400 transition-colors">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">MOTORISTA</div>
            <div className="text-4xl font-extrabold mt-2" style={{ fontFamily: "Manrope" }}>R$ 25,90<span className="text-base font-normal text-slate-500">/mês</span></div>
            <p className="text-sm text-slate-600 mt-1">Autônomo, freteiro ou empregado que quer controle próprio.</p>
            <ul className="mt-5 space-y-2 text-sm">
              {["Até 5 veículos", "Todos os relatórios + PDF", "PIX à vista mensal", "Convide sua empresa e ganhe 50% OFF", "Cancele quando quiser"].map((t) => (
                <li key={t} className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <Link to="/register"><Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">Começar grátis</Button></Link>
            <Link to="/assinar?plan=acerto_rapido_autonomo_annual"><Button variant="outline" className="w-full mt-2 border-2 border-blue-200 hover:bg-blue-50 h-10 text-sm font-bold text-blue-700">Assinar agora sem trial →</Button></Link>
            <p className="text-center text-xs text-slate-500 mt-2">Anual R$ 310,80 · <strong className="text-emerald-700">PIX 25% OFF R$ 233,10</strong></p>
          </Card>
          <Card className="p-8 border-2 border-yellow-500 shadow-xl relative">
            <Badge className="absolute -top-3 right-6 bg-yellow-500 text-black gap-1"><Star size={12} weight="fill" /> MAIS ESCOLHIDO</Badge>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">EMPRESA</div>
            <div className="text-4xl font-extrabold mt-2" style={{ fontFamily: "Manrope" }}>R$ 59,90<span className="text-base font-normal text-slate-500">/mês</span></div>
            <p className="text-sm text-slate-600 mt-1">Frota completa com múltiplos motoristas e veículos.</p>
            <ul className="mt-5 space-y-2 text-sm">
              {["Até 150 veículos + 150 motoristas", "GPS em tempo real dos caminhões", "Ranking de motoristas + análise km/L", "Exportação Excel mensal pro contador", "Cartão em até 3x no plano anual", "Convide motoristas e ganhe 50% OFF"].map((t) => (
                <li key={t} className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <Link to="/register"><Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">Começar grátis</Button></Link>
            <Link to="/assinar?plan=acerto_rapido_annual"><Button variant="outline" className="w-full mt-2 border-2 border-blue-200 hover:bg-blue-50 h-10 text-sm font-bold text-blue-700">Assinar agora sem trial →</Button></Link>
            <p className="text-center text-xs text-slate-500 mt-2">Anual R$ 718,80 · <strong className="text-emerald-700">PIX 25% OFF R$ 539,10</strong></p>
          </Card>
        </div>
      </Section>

      {/* Testimonials */}
      <Section id="depoimentos">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Quem já usa recomenda</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <Card key={t.name} className="p-6 border-2 border-blue-100 bg-white">
              <div className="flex gap-1 mb-3">{[0,1,2,3,4].map((i) => <Star key={i} size={14} weight="fill" className="text-yellow-500" />)}</div>
              <p className="text-slate-700 text-sm leading-relaxed">"{t.text}"</p>
              <div className="mt-4 border-t pt-3 flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold flex items-center justify-center">{t.name[0]}</div>
                <div>
                  <div className="text-sm font-bold">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-slate-50">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>Perguntas frequentes</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {FAQ.map((f) => (
            <details key={f.q} className="group bg-white border rounded-lg p-4 cursor-pointer">
              <summary className="font-bold text-slate-900 flex justify-between items-center list-none">
                <span>{f.q}</span>
                <span className="text-blue-600 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-sm text-slate-600 mt-2">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 text-white">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold" style={{ fontFamily: "Manrope" }}>Pronto pra deixar o caderninho?</h2>
          <p className="text-lg text-blue-100 mt-3">Testa 7 dias sem cartão. Se não gostar, é só não continuar.</p>
          <Link to="/register"><Button className="mt-6 bg-yellow-400 text-blue-900 hover:bg-yellow-300 hover:text-blue-900 font-extrabold h-12 px-8 text-base gap-2">
            <Rocket size={20} weight="fill" /> Criar conta grátis agora
          </Button></Link>
          <p className="text-xs text-blue-200 mt-3">Sem cartão · Cancele quando quiser · Suporte por WhatsApp</p>
        </div>
      </Section>

      {/* Floating WhatsApp Share FAB */}
      <a
        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
          `🚛 *ACERTO RÁPIDO JVO* — o app do caminhoneiro brasileiro\n\n` +
          `✅ Acerto de viagem pronto em minutos\n` +
          `✅ GPS em tempo real dos caminhões\n` +
          `✅ Relatórios em PDF pro contador\n` +
          `✅ Cartão em até 3x ou *PIX com 25% OFF*\n\n` +
          `👉 Baixe agora, 7 dias grátis:\n${typeof window !== "undefined" ? window.location.origin : ""}/assinar`
        )}`}
        target="_blank"
        rel="noreferrer"
        data-testid="landing-whatsapp-fab"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 shadow-2xl shadow-emerald-600/40 transition-all hover:scale-105"
        title="Compartilhar no WhatsApp"
      >
        <WhatsappLogo size={26} weight="fill" />
        <span className="hidden sm:inline text-sm font-bold pr-1">Compartilhar</span>
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
        </span>
      </a>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Logo size={36} /><div><div className="font-bold text-white text-sm">Acerto Rápido JVO</div><div className="text-xs">Feito pra caminhoneiro brasileiro</div></div></div>
          <div className="flex flex-wrap gap-4 text-xs">
            <Link to="/assinar" className="hover:text-white">Assinar</Link>
            <Link to="/como-indicar" className="hover:text-white">Indique e ganhe</Link>
            <a href="#precos" className="hover:text-white">Preços</a>
            <Link to="/termos" className="hover:text-white">Termos de Uso</Link>
            <Link to="/privacidade" className="hover:text-white">Privacidade (LGPD)</Link>
            <Link to="/login" className="hover:text-white">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
